import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getMediaTutorialService } from "../../../../services/quiz/quiz.service";
import {
  isTutorialVisto,
  marcarTutorialVisto,
} from "../../../../utils/tutorialWatched";
import "./tutorial.css";

const normalizeUrl = (raw) => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
};

const getYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?]+)/,
    /youtube\.com\/embed\/([^?]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const pickUrl = (block, fallback) =>
  normalizeUrl(
    block?.video_url_generada || block?.video_url_externa || block?.video_url || fallback,
  );

const KIND_META = {
  tutorial: { label: "Bienvenida", icon: "solar:play-circle-bold" },
  instrucciones: { label: "Instrucciones", icon: "solar:book-2-bold" },
};

/**
 * Pantalla introductoria con video(s) tutorial antes de iniciar un módulo de quiz.
 * Bloquea "Comenzar" hasta que todos los videos disponibles hayan sido vistos.
 *
 * @param {string} tipo - 'inicio_confirmacion' | 'inicio_fortaleza' | 'inicio_aplicacion'
 * @param {string} titulo - Nombre del módulo a mostrar
 * @param {Function} onStart - Callback al hacer clic en "Comenzar"
 */
const QuizVideoIntro = ({ tipo, titulo, onStart, continuar = false }) => {
  const userId = useSelector((s) => s.auth?.user?.user_id || s.auth?.user?.id);
  const videoRef = useRef(null);
  const [videos, setVideos] = useState({ tutorial: null, instrucciones: null });
  const [activeKind, setActiveKind] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vistos, setVistos] = useState({});

  useEffect(() => {
    // Sólo pedir los tutoriales si el usuario está autenticado (evita 403).
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      try {
        const data = await getMediaTutorialService(tipo);
        const tutorial = pickUrl(
          data?.tutorial,
          data?.video_tutorial_url_generada || data?.video_url,
        );
        const instrucciones = pickUrl(data?.instrucciones);
        setVideos({ tutorial, instrucciones });
        if (tutorial) setActiveKind("tutorial");
        else if (instrucciones) setActiveKind("instrucciones");
      } catch {
        // Sin video — no requiere ver nada.
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tipo, userId]);

  // Hidratar `vistos` desde localStorage cuando ya conocemos los videos.
  useEffect(() => {
    if (loading) return;
    const inicial = {};
    Object.keys(videos).forEach((k) => {
      if (videos[k]) inicial[k] = isTutorialVisto(userId, tipo, k);
    });
    setVistos(inicial);
  }, [loading, videos, tipo, userId]);

  const marcarVisto = (kind) => {
    if (!kind) return;
    marcarTutorialVisto(userId, tipo, kind);
    setVistos((prev) => ({ ...prev, [kind]: true }));
  };

  const availableKinds = Object.keys(videos).filter((k) => videos[k]);
  const videoUrl = activeKind ? videos[activeKind] : null;
  const pendientes = availableKinds.filter((k) => !vistos[k]);
  const puedeIniciar = pendientes.length === 0;

  const handleStart = () => {
    if (!puedeIniciar) {
      const nombres = pendientes.map((k) => KIND_META[k].label).join(" y ");
      toast.warning(
        pendientes.length === 1
          ? `Debes ver el video de ${nombres} antes de comenzar.`
          : `Debes ver los videos (${nombres}) antes de comenzar.`,
      );
      // Cambia al primer pendiente para facilitar.
      setActiveKind(pendientes[0]);
      return;
    }
    onStart();
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              margin: "0 auto 1rem",
              border: "4px solid rgba(255,255,255,0.2)",
              borderTopColor: "#7c3aed",
              borderRadius: "50%",
              animation: "spin 0.9s linear infinite",
            }}
          />
          <div style={{ fontWeight: 600, letterSpacing: "0.02em" }}>
            Cargando video...
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const youtubeId = getYoutubeId(videoUrl);
  const embedUrl = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`
    : null;

  const activeVisto = activeKind ? vistos[activeKind] : false;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
      }}
    >
      {/* Título */}
      <div className="text-center mb-4">
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(119,73,248,0.2)",
            border: "2px solid rgba(119,73,248,0.5)",
            marginBottom: "1rem",
          }}
        >
          <Icon icon="solar:play-circle-bold" style={{ fontSize: 32, color: "#7749F8" }} />
        </div>
        <h2 style={{ color: "white", fontWeight: 700, marginBottom: "0.25rem" }}>
          {titulo}
        </h2>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.95rem" }}>
          {continuar
            ? "Puedes retomar tu progreso desde donde lo dejaste."
            : availableKinds.length > 0
              ? "Debes ver el video completo antes de comenzar."
              : "Cuando estés listo, haz clic en el botón para comenzar."}
        </p>
      </div>

      {/* Tabs solo si hay más de un video */}
      {availableKinds.length > 1 && (
        <div
          className="d-inline-flex gap-2 mb-3 p-1 rounded-pill"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          {availableKinds.map((k) => {
            const active = activeKind === k;
            const visto = vistos[k];
            return (
              <button
                key={k}
                onClick={() => setActiveKind(k)}
                className="d-inline-flex align-items-center gap-2"
                style={{
                  padding: "0.4rem 1.1rem",
                  fontSize: "0.85rem",
                  border: "none",
                  borderRadius: 999,
                  background: active
                    ? "linear-gradient(135deg, #7749F8, #5b34c1)"
                    : "transparent",
                  color: active ? "white" : "rgba(255,255,255,0.7)",
                  cursor: "pointer",
                  fontWeight: active ? 600 : 500,
                  transition: "all 0.15s",
                }}
              >
                <Icon icon={KIND_META[k].icon} style={{ fontSize: 16 }} />
                {KIND_META[k].label}
                {visto && (
                  <Icon
                    icon="mdi:check-circle"
                    style={{ fontSize: 14, color: "#10b981" }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Reproductor */}
      {videoUrl && (
        <div
          key={activeKind}
          style={{
            width: "100%",
            maxWidth: 720,
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
            marginBottom: "1rem",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {embedUrl ? (
            <div className="ratio ratio-16x9">
              <iframe
                src={embedUrl}
                title={titulo}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ border: 0 }}
              />
            </div>
          ) : (
            <div className="ratio ratio-16x9" style={{ background: "#000" }}>
              <video
                key={activeKind}
                ref={videoRef}
                src={videoUrl}
                controls
                playsInline
                webkit-playsinline="true"
                preload="metadata"
                onEnded={() => marcarVisto(activeKind)}
                onError={() => {
                  // Sin fuente compatible — permitimos al usuario marcar
                  // manualmente ("Ya vi este video") para no bloquearlo.
                }}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          )}
        </div>
      )}

      {/* Confirmación de visto (para YouTube o si el usuario prefiere marcar manualmente) */}
      {videoUrl && !activeVisto && (
        <button
          onClick={() => marcarVisto(activeKind)}
          className="d-inline-flex align-items-center gap-2 mb-3"
          style={{
            padding: "0.5rem 1.2rem",
            background: "rgba(16,185,129,0.15)",
            border: "1px solid rgba(16,185,129,0.4)",
            borderRadius: 999,
            color: "#10b981",
            fontSize: "0.85rem",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <Icon icon="mdi:check-circle-outline" style={{ fontSize: 16 }} />
          Ya vi este video
        </button>
      )}

      {videoUrl && activeVisto && (
        <div
          className="d-inline-flex align-items-center gap-2 mb-3"
          style={{
            padding: "0.4rem 1rem",
            background: "rgba(16,185,129,0.1)",
            borderRadius: 999,
            color: "#10b981",
            fontSize: "0.82rem",
            fontWeight: 600,
          }}
        >
          <Icon icon="mdi:check-circle" style={{ fontSize: 16 }} />
          Video marcado como visto
        </div>
      )}

      {/* Aviso de pendientes */}
      {!puedeIniciar && availableKinds.length > 1 && (
        <div
          style={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.82rem",
            marginBottom: "0.75rem",
          }}
        >
          Pendiente{pendientes.length > 1 ? "s" : ""}:{" "}
          {pendientes.map((k) => KIND_META[k].label).join(", ")}
        </div>
      )}

      {/* Botón comenzar */}
      <button
        onClick={handleStart}
        disabled={!puedeIniciar}
        title={
          puedeIniciar
            ? undefined
            : "Debes ver el video completo antes de comenzar"
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.6rem",
          padding: "0.85rem 2rem",
          fontSize: "clamp(0.95rem, 2.4vw, 1.05rem)",
          fontWeight: 600,
          color: "white",
          background: puedeIniciar
            ? "linear-gradient(135deg, #7749F8, #5b34c1)"
            : "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: 50,
          cursor: puedeIniciar ? "pointer" : "not-allowed",
          boxShadow: puedeIniciar
            ? "0 4px 20px rgba(119,73,248,0.4)"
            : "none",
          transition: "transform 0.15s, box-shadow 0.15s",
          opacity: puedeIniciar ? 1 : 0.7,
          maxWidth: "100%",
          lineHeight: 1.25,
          textAlign: "center",
          whiteSpace: "normal",
        }}
        onMouseEnter={(e) => {
          if (!puedeIniciar) return;
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 8px 28px rgba(119,73,248,0.55)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "";
          e.currentTarget.style.boxShadow = puedeIniciar
            ? "0 4px 20px rgba(119,73,248,0.4)"
            : "none";
        }}
      >
        <Icon
          icon={
            !puedeIniciar
              ? "mdi:lock-outline"
              : continuar
                ? "solar:arrow-right-bold"
                : "solar:bolt-bold"
          }
          style={{ fontSize: 20 }}
        />
        {tipo === "inicio_fortaleza" || tipo === "inicio_aplicacion"
          ? "Sigamos construyendo tus 7 fortalezas"
          : continuar
            ? "Continuar"
            : "Comenzar ahora"}
      </button>
    </div>
  );
};

export default QuizVideoIntro;
